"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { MoonlightHomePage } from "~/app/components/moonlight-marketplace/moonlight-home-page";
import { toast } from "sonner";

/** Inbound GPT / EasyGPT marketing home (client) — Book Demo modal + Moonlight hero. */
export function InboundHomeLanding() {
  const [demoStep, setDemoStep] = useState(1);
  const [demoFormData, setDemoFormData] = useState({
    email: "",
    practiceName: "",
    firstName: "",
    lastName: "",
    websiteUrl: "",
  });
  const [isBookDemoModalOpen, setIsBookDemoModalOpen] = useState(false);
  const calendlyParentRef = useRef<HTMLDivElement | null>(null);
  const calendlyUrl = useMemo(
    () => "https://calendly.com/siteglue/inboundgpt",
    [],
  );

  useEffect(() => {
    const handleOpenModal = (event: Event) => {
      setIsBookDemoModalOpen(true);
      setDemoStep(1);
      const detail = (event as CustomEvent<Partial<typeof demoFormData>>).detail;
      if (detail && typeof detail === "object") {
        setDemoFormData((prev) => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(detail).filter(([, v]) => typeof v === "string"),
          ),
        }));
      }
    };
    window.addEventListener("openBookDemoModal", handleOpenModal);
    return () => window.removeEventListener("openBookDemoModal", handleOpenModal);
  }, []);

  useEffect(() => {
    if (!isBookDemoModalOpen || demoStep !== 2) return;
    if (!calendlyParentRef.current) return;
    calendlyParentRef.current.innerHTML = "";
    const prefillName = `${demoFormData.firstName} ${demoFormData.lastName}`.trim();
    const customAnswers: Record<string, string> = {};
    if (demoFormData.practiceName) customAnswers.a1 = demoFormData.practiceName;
    if (demoFormData.websiteUrl) customAnswers.a2 = demoFormData.websiteUrl;
    const init = () => {
      const Calendly = (window as Window & { Calendly?: { initInlineWidget: (o: object) => void } }).Calendly;
      if (!Calendly?.initInlineWidget) return;
      Calendly.initInlineWidget({
        url: calendlyUrl,
        parentElement: calendlyParentRef.current,
        prefill: {
          email: demoFormData.email || undefined,
          firstName: demoFormData.firstName || undefined,
          lastName: demoFormData.lastName || undefined,
          name: prefillName || undefined,
          customAnswers: Object.keys(customAnswers).length ? customAnswers : undefined,
        },
      });
    };
    const existing = document.getElementById("calendly-widget-js") as HTMLScriptElement | null;
    if ((window as Window & { Calendly?: unknown }).Calendly) {
      init();
      return;
    }
    if (existing) {
      existing.addEventListener("load", init, { once: true });
      return;
    }
    const script = document.createElement("script");
    script.id = "calendly-widget-js";
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    script.addEventListener("load", init, { once: true });
    document.body.appendChild(script);
  }, [
    isBookDemoModalOpen,
    demoStep,
    demoFormData.email,
    demoFormData.firstName,
    demoFormData.lastName,
    demoFormData.practiceName,
    demoFormData.websiteUrl,
    calendlyUrl,
  ]);

  const handleDemoStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch("/api/demo-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(demoFormData),
      });
      if (response.ok) {
        setDemoStep(2);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <>
      <MoonlightHomePage />

      <Dialog open={isBookDemoModalOpen} onOpenChange={setIsBookDemoModalOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="mb-2 text-3xl">Schedule Your Demo</DialogTitle>
            <DialogDescription className="text-lg">
              See Moonlight Marketplace — browse agents, clone templates, and deploy
              phone and chat.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {demoStep === 1 ? (
              <form onSubmit={handleDemoStep1Submit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      First Name<span className="text-red-500">*</span>
                    </label>
                    <Input
                      required
                      value={demoFormData.firstName}
                      onChange={(e) =>
                        setDemoFormData({ ...demoFormData, firstName: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      Last Name<span className="text-red-500">*</span>
                    </label>
                    <Input
                      required
                      value={demoFormData.lastName}
                      onChange={(e) =>
                        setDemoFormData({ ...demoFormData, lastName: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    Work Email<span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    required
                    value={demoFormData.email}
                    onChange={(e) =>
                      setDemoFormData({ ...demoFormData, email: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    Business Name<span className="text-red-500">*</span>
                  </label>
                  <Input
                    required
                    value={demoFormData.practiceName}
                    onChange={(e) =>
                      setDemoFormData({ ...demoFormData, practiceName: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Website URL</label>
                  <Input
                    type="url"
                    placeholder="https://yourbusiness.com"
                    value={demoFormData.websiteUrl}
                    onChange={(e) =>
                      setDemoFormData({ ...demoFormData, websiteUrl: e.target.value })
                    }
                  />
                </div>
                <Button type="submit" className="w-full" size="lg">
                  Continue to scheduling
                </Button>
              </form>
            ) : (
              <div ref={calendlyParentRef} style={{ minHeight: 630 }} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
